'use client';
import LogoUtua from '@/img/LogoUTUA.svg';
import { Link } from '@/navigation';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScore';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import AppShortcutIcon from '@mui/icons-material/AppShortcut';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { ElementType, useEffect, useRef, useState } from 'react';
import style from './style.module.css';

const getNavItems = (locale: string) => {
  const excludedLocalesCreditCard = ['id-ID', 'th-TH', 'en-BD', 'ar-MA'];
  const excludedLocalesLoan = ['es-CO'];

  const creditCardNavItem = {
    url: '/cartao-de-credito',
    className: style['credit_card'],
    label: 'menu.linkCards',
    Icon: CreditScoreOutlinedIcon,
  };
  const loanNavItem = {
    url: '/emprestimo',
    className: style['loan'],
    label: 'menu.linkLoan',
    Icon: SavingsOutlinedIcon,
  };

  const generalNavItems = [
    {
      url: '/blog',
      className: style['blog'],
      label: 'menu.linkBlog',
      Icon: HistoryEduIcon,
    },
    {
      url: '/quem-somos',
      className: style['sobre-nos'],
      label: 'menu.linkAboutUs',
      Icon: HubOutlinedIcon,
    },
    {
      url: '/contato',
      className: style['contato'],
      label: 'menu.linkContact',
      Icon: PhoneOutlinedIcon,
    },
  ];

  // Novo item de menu para pt-BR
  const specialNavItemForPtBR = {
    url: 'https://clube.utua.com.br',
    className: style['utua-app'],
    label: 'menu.linkApp',
    Icon: AppShortcutIcon,
  };

  const navItems = [...generalNavItems];

  if (locale === 'pt-BR') {
    navItems.unshift(specialNavItemForPtBR);
  }

  if (!excludedLocalesCreditCard.includes(locale)) {
    navItems.unshift(creditCardNavItem);
  }

  if (!excludedLocalesLoan.includes(locale)) {
    navItems.unshift(loanNavItem);
  }

  return navItems;
};

const capitalize = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

interface NavItemComponentProps {
  className?: string;
  label: string;
  id?: string;
  url?: string;
  onClick?: () => void;
  target?: '_blank' | '_self' | '_parent' | '_top' | 'framename';
  active?: boolean;
  Icon: ElementType;
  type?: string;
  element?: ElementType;
}

const NavItem = React.forwardRef<HTMLSpanElement, NavItemComponentProps>(
  (
    {
      className,
      label,
      id,
      url,
      onClick,
      target,
      active,
      Icon,
      type,
      element: Element,
      ...rest
    },
    ref
  ) => {
    const AnchorElement = Element || (url ? Link : 'span');
    const itemClassNames = classNames(style.navItem, className);

    return (
      <li className={itemClassNames}>
        <AnchorElement
          onClick={onClick}
          target={target}
          className={style.navItemAnchor}
          href={url}
          passhref="true"
          {...rest}>
          <div className={style.navItemIcon}>
            <Icon />
          </div>
          <span id={id} className={style.navItemLabel} ref={ref}>
            {label}
          </span>
        </AnchorElement>
      </li>
    );
  }
);

NavItem.displayName = 'NavItem';

interface NavIconProps {
  closeIcon: boolean;
  className?: string;
  onClick: () => void;
}

const NavIcon = ({ closeIcon, onClick, ...rest }: NavIconProps) => (
  <div
    className={classNames(style.navIcon, {
      [style.closeIcon]: Boolean(closeIcon),
    })}>
    <button
      className={style.icon}
      onClick={onClick}
      {...rest}
      aria-label="navigation button">
      <span className={style.iconSpan} />
      <span className={style.iconSpan} />
      <span className={style.iconSpan} />
      <span className={style.iconSpan} />
    </button>
  </div>
);

interface LogoProps {
  onClick: () => void;
}

const Logo = ({ onClick }: LogoProps) => {
  const locale = useLocale();
  const getIconLink = () => {
    if (locale === 'pt-BR') {
      return 'https://utua.com.br';
    }

    return `https://utua.com.br/${locale.slice(-2).toLowerCase()}`;
  };

  return (
    <>
      <a
        href={getIconLink()}
        aria-label="Go to Utua home page"
        className={style.headerLogoSmall}>
        <Image
          onClick={onClick}
          className={style.headerLogo}
          src={LogoUtua}
          alt="Logo Utua"
          width={61}
          height={30}
        />
      </a>
      <a
        href={getIconLink()}
        aria-label="Go to Utua home page"
        className={style.headerLogoLarge}>
        <Image
          onClick={onClick}
          className={style.headerLogo}
          src={LogoUtua}
          width={61}
          height={30}
          alt="Logo Utua"
        />
      </a>
    </>
  );
};

interface MenuProps {
  fixed?: boolean;
  className?: string;
  openFrom?: 'top' | 'left';
  type?: string;
  transparent?: boolean;
  navLinkElement?: ElementType;
}

const Menu = ({
  fixed,
  className,
  openFrom = 'left',
  type = 'primary',
  transparent = false,
  navLinkElement,
}: MenuProps) => {
  const t = useTranslations('Index');
  const locale = useLocale();
  const [navOpened, setNavOpened] = useState(false);
  const containerClassnames = classNames(
    className,
    style.container,
    style[`openFrom${capitalize(openFrom)}`],
    {
      [style.opened]: navOpened,
      [style.fixed]: fixed,
    },
    style[`navHeader-type-${type}`],
    {
      [style.typeTransparent]: transparent,
    }
  );

  const closeNavHeader = () => {
    setNavOpened(false);
    document.body.style.overflow = 'auto'; // Reativa o scroll do body
  };

  const toggleNavOpened = () => {
    setNavOpened(!navOpened);
    if (!navOpened) {
      document.body.style.overflow = 'hidden'; // Desativa o scroll do body
    } else {
      document.body.style.overflow = 'auto'; // Reativa o scroll do body
    }
  };

  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
    setIsButtonMenuChatClicked(!isButtonMenuChatClicked);
  };

  const [isButtonMenuChatClicked, setIsButtonMenuChatClicked] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  // Função para verificar se o clique foi fora do form
  const handleClickOutside = (event: MouseEvent) => {
    if (formRef.current && !formRef.current.contains(event.target as Node)) {
      setIsVisible(false);
      setIsButtonMenuChatClicked(false);
    }
  };

  // Adicionando o event listener
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Removendo o event listener no cleanup
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = getNavItems(locale);

  return (
    <div className={containerClassnames}>
      <div
        className={classNames(style.header, {
          [style.headerWithHeight]: isButtonMenuChatClicked,
        })}>
        <div
          className={classNames(style.navContainer, {
            [style.typeTransparent]: transparent,
          })}>
          <div className={style.headerActions}>
            <Logo onClick={closeNavHeader} />
          </div>

          <div className={style.contentMenu}>
            <nav
              className={classNames(style.nav, {
                [style.typeTransparent]: transparent,
              })}>
              <div className={style.navBackground} />
              <div className={style.menuList}>
                <ul className={style.navList}>
                  {navItems.map((item) => (
                    <NavItem
                      key={item.url}
                      {...item}
                      label={t(item.label)}
                      element={navLinkElement}
                      onClick={closeNavHeader}
                    />
                  ))}
                </ul>
              </div>
            </nav>

            <form
              className={style.formChatMenu}
              ref={formRef}
              method="get"
              action="https://chat.utua.com.br">
              <div
                className={classNames(style.formChatMenuField, {
                  [style.formChatMenuFieldVisible]: isVisible,
                })}>
                <AutoAwesomeIcon className={style.iconInput} />
                <input
                  className={style.inputChatMenu}
                  aria-label="Type here your message"
                  name="q"
                  type="text"
                  placeholder={t('menu.chatPlaceholder')}
                />
                <button
                  className={style.buttonMenuChatInside}
                  aria-label="Submit Question Button">
                  <SendOutlinedIcon />
                </button>
                <button
                  className={style.buttonClose}
                  type="button"
                  onClick={toggleVisibility}>
                  <CloseIcon />
                </button>
              </div>

              {!isVisible && (
                <div>
                  <button
                    type="button"
                    className={style.buttonMenuChat}
                    aria-label="Utua Chat Button"
                    onClick={toggleVisibility}>
                    <AutoAwesomeIcon />
                  </button>
                </div>
              )}
            </form>
          </div>
          <NavIcon closeIcon={navOpened} onClick={toggleNavOpened} />
        </div>
      </div>
      <div className={style.overlay} onClick={toggleNavOpened} />
    </div>
  );
};

export default Menu;
